import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import { useState, useContext, useEffect, Activity } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Button from '@mui/material/Button';

const logo = new URL('../../../public/crx-stacked.png', import.meta.url).href;
import { ClinicRxContext } from '../../App';
import ResetPasswordPopover from './ResetPasswordPopover';

const undevelopedPages = [
  {
    page: 'Profile',
    route: '/profile',
    protected: true,
    title: 'Edit your Profile',
  },
];

const loginPage = [
  {
    page: 'Login',
    route: '/',
    protected: false,
    title: 'Login',
  },
];

const menuPages = [
  {
    page: 'Find an Appt',
    route: '/alert',
    protected: true,
    userTypes: [0, 1],
    title: 'Find an Appointment',
  },
  {
    page: 'Dashboard',
    route: '/dashboard',
    protected: true,
    userTypes: [0, 1, 2, 3],
    title: 'Recent Alerts',
  },
  {
    page: 'Charts',
    route: '/charts',
    protected: true,
    userTypes: [0, 1, 2, 3],
    title: 'View Alert Data',
  },
];

const allPages = [...menuPages, ...loginPage];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { state, dispatch } = useContext(ClinicRxContext);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    const currentPage = allPages.find(
      (page) => page.route === location.pathname,
    );
    if (currentPage?.title) {
      dispatch({ type: 'set-page-title', title: currentPage.title });
    }
  }, [location]);

  const getMenuOptions = () => {
    if (state.user.userType === null) return loginPage;
    return menuPages.filter((page) => {
      return page.userTypes.includes(state.user.userType);
    });
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
  };

  return (
    <Box
      component='header'
      sx={{
        minHeight: '150px',
        fontColor: theme.palette.grey['50'],
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '5px',
          width: '100%',
          background: `linear-gradient(to right, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
        }}
      >
        <Box
          component='ul'
          sx={{
            paddingTop: '15px',
            paddingLeft: '20px',
            height: '70px',
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                component='li'
                sx={{
                  cursor: location.pathname === '/' ? 'auto' : 'pointer',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '20px',
                  color: theme.palette.grey['50'],
                  alignItems: 'center',
                }}
                onClick={() => {
                  // Menu doesn't open on login page.
                  if (location.pathname === '/') return;
                  setButtonClicked((clicked) => !clicked);
                }}
              >
                <MenuIcon fontSize='large' />
                <Typography
                  sx={{
                    fontSize: '22px',
                    fontWeight: 'bold',
                    textShadow: `1px 1px ${theme.palette.grey['600']}`,
                    textDecoration: buttonClicked ? 'underline' : '',
                  }}
                >
                  Menu
                </Typography>
              </Box>
              <Box
                sx={{
                  display: buttonClicked ? 'block' : 'none',
                  zIndex: 1,
                  borderRadius: '20px',
                  padding: '20px',
                  opacity: '100%',
                  backgroundColor: theme.palette.grey['300'],
                  boxShadow: '5px 5px 5px #808080',
                  width: '200px',
                  paddingLeft: '5px',
                }}
              >
                <Box
                  component='ul'
                  sx={{ listStyle: 'none' }}
                >
                  {getMenuOptions().map(
                    ({ page, route, protected: pro }, idx) => (
                      <Box
                        component='li'
                        key={idx}
                        sx={{
                          height: '50px',
                          padding: '15px',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: `${theme.palette.primary.light}`,
                            opacity: 'inherit',
                          },
                        }}
                        onClick={() => {
                          if (pro && !state.user.isAuthenticated) return;
                          if (route) {
                            navigate(route);
                            setButtonClicked(false);
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 'bolder',
                            color: theme.palette.grey['900'],
                          }}
                        >
                          {page}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box>
          <Box
            component='img'
            sx={{
              backgroundColor: theme.palette.grey['100'],
              height: '100px',
              width: '140px',
              cursor: 'pointer',
              borderRadius: '15px',
            }}
            src={logo}
            alt='ClinicRx logo'
            onClick={() => {
              if (state.user.isAuthenticated) navigate('/dashboard');
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          background: `linear-gradient(to left, ${theme.palette.grey['700']},${theme.palette.grey['300']})`,
          minHeight: '120px',
          borderRadius: '0 0 5px 5px',
          boxShadow: `0 4px 8px ${theme.palette.grey['500']}`,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '5px',
          color: theme.palette.grey['300'],
        }}
      >
        <Activity mode={state.headerTitle ? 'visible' : 'hidden'}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              marginRight: 'auto',
              gap: '5px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '5px',
                fontWeight: 'bold',
                color: 'primary.main',
                paddingLeft: '30px',
              }}
            >
              <Typography sx={{ fontSize: '22px' }}>
                {state.headerTitle}
              </Typography>
            </Box>
          </Box>
        </Activity>
        <Activity mode={state.user.name.first ? 'visible' : 'hidden'}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-end',
              marginLeft: 'auto',
              gap: '5px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '5px',
                fontSize: '35px',
              }}
            >
              <AccountCircleIcon
                sx={{ cursor: 'pointer' }}
                onClick={(e) => {
                  setPopoverAnchor(e.currentTarget);
                }}
              />
              <ResetPasswordPopover
                popoverAnchor={popoverAnchor}
                handleClosePopover={handleClosePopover}
                resetPassword={resetPassword}
                setResetPassword={setResetPassword}
              />
              <Typography>
                {state.user.name.first?.concat(` ${state.user.name.last}`)}
              </Typography>
            </Box>
            <Typography>{state.user.email}</Typography>
            <Typography>{state.user.organization}</Typography>
            <Button
              variant='text'
              color='info'
              onClick={() => {
                dispatch({ type: 'logout' });
              }}
              sx={{ padding: 0 }}
            >
              Log out
            </Button>
          </Box>
        </Activity>
      </Box>
    </Box>
  );
}
